import { useState, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  DataTable,
  EmptyState,
  Heading,
  SearchInput,
  Text,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { formatTimestampDate } from "@/app/dateFormat";
import { useListBranchesFullQuery } from "../api/branches.api";
import type { Branch } from "../setup.types";
import { BranchFormDialog } from "./BranchFormDialog";
import { DeleteBranchDialog } from "./DeleteBranchDialog";

const PAGE_SIZE = 10;

type SortKey = "readableId" | "name" | "code" | "createdAt";
type SortDirection = "asc" | "desc";

function ChevronUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ChevronsUpDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
    </svg>
  );
}

function EditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
    </svg>
  );
}

function SortableHeader({
  label,
  sortKey,
  activeSortKey,
  direction,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
}) {
  const isActive = activeSortKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 whitespace-nowrap hover:text-text ${isActive ? "text-text" : ""}`}
      aria-label={label}
    >
      {label}
      {isActive ? (
        direction === "asc" ? (
          <ChevronUpIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronDownIcon className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDownIcon className="h-3.5 w-3.5 text-text-subtle" />
      )}
    </button>
  );
}

function compareBranches(a: Branch, b: Branch, key: SortKey, direction: SortDirection) {
  const dir = direction === "asc" ? 1 : -1;
  switch (key) {
    case "createdAt":
      return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    case "readableId":
      return (a.readableId ?? "").localeCompare(b.readableId ?? "") * dir;
    case "name":
      return a.name.localeCompare(b.name) * dir;
    case "code":
      return (a.code ?? "").localeCompare(b.code ?? "") * dir;
  }
}

export function BranchesTab() {
  const intl = useIntl();
  const { data: branches, isLoading } = useListBranchesFullQuery();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [deletingBranch, setDeletingBranch] = useState<Branch | null>(null);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }

  const searched = branches
    ? branches.filter((branch) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          branch.name.toLowerCase().includes(q) ||
          branch.readableId?.toLowerCase().includes(q) ||
          branch.code?.toLowerCase().includes(q) ||
          branch.address?.toLowerCase().includes(q)
        );
      })
    : null;

  const filtered = searched && sortKey ? [...searched].sort((a, b) => compareBranches(a, b, sortKey, sortDirection)) : searched;

  const totalCount = filtered?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filtered?.slice(startIndex, startIndex + PAGE_SIZE) ?? [];
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  const columns: DataTableColumn<Branch>[] = [
    {
      id: "readableId",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.branches.columnId" })}
          sortKey="readableId"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (branch) => <span className={`font-medium ${tabularNumsClass}`}>{branch.readableId}</span>,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "name",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.branches.columnName" })}
          sortKey="name"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (branch) => branch.name,
      cellClassName: "whitespace-nowrap font-medium text-text",
    },
    {
      id: "code",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.branches.columnCode" })}
          sortKey="code"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (branch) => branch.code ?? "—",
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "address",
      header: intl.formatMessage({ id: "lender.branches.columnAddress" }),
      cell: (branch) => branch.address ?? "—",
    },
    {
      id: "createdAt",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.branches.columnCreatedAt" })}
          sortKey="createdAt"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (branch) => formatTimestampDate(branch.createdAt),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.branches.columnAction" }),
      cell: (branch) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "lender.branches.actionEdit" })}
            onClick={() => setEditingBranch(branch)}
            className="inline-flex items-center justify-center text-text-muted hover:text-accent-600"
          >
            <EditIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "lender.branches.actionDelete" })}
            onClick={() => setDeletingBranch(branch)}
            className="inline-flex items-center justify-center text-text-muted hover:text-error"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>
          <FormattedMessage id="lender.branches.heading" />
        </Heading>
        <Button type="button" onClick={() => setIsAddOpen(true)}>
          <FormattedMessage id="lender.branches.addButton" />
        </Button>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => {
            setQuery("");
            setPage(1);
          }}
          placeholder={intl.formatMessage({ id: "lender.branches.searchPlaceholder" })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(branch) => branch.id}
        loading={isLoading}
        titleColumnId="name"
        metaColumnId="readableId"
        emptyState={
          <EmptyState>
            <FormattedMessage id={query ? "lender.branches.noMatchingBranchesMessage" : "lender.branches.emptyStateMessage"} />
          </EmptyState>
        }
      />

      {totalCount > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <Text size="sm" variant="muted">
            <FormattedMessage id="lender.creditor.paginationRangeLabel" values={{ start: rangeStart, end: rangeEnd, total: totalCount }} />
          </Text>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
              className="text-text-muted disabled:opacity-40"
            >
              ‹
            </button>
            <button
              type="button"
              disabled={page >= pageCount}
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
              className="text-text-muted disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>
      )}

      <BranchFormDialog open={isAddOpen} onOpenChange={setIsAddOpen} />
      {editingBranch && (
        <BranchFormDialog open={editingBranch !== null} onOpenChange={() => setEditingBranch(null)} branch={editingBranch} />
      )}
      {deletingBranch && (
        <DeleteBranchDialog open={deletingBranch !== null} onOpenChange={() => setDeletingBranch(null)} branch={deletingBranch} />
      )}
    </div>
  );
}
